import { IPost } from '_interfaces/post.interface';
import { IPostType } from '_interfaces/systemPost.types';
import { IPostStatus } from '_interfaces/types/post.types';
import { filteredPostCommentsAtom } from 'atoms/postComments';
import { commentMatchesSearch, postMatchesSearch } from 'atoms/utils/searchUtils';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { atom } from 'jotai';
import { atomFamily, atomWithObservable, atomWithStorage, loadable } from 'jotai/utils';
import { Observable } from 'rxjs';
import { auth, db, refreshFirebaseToken } from 'utils/firebase';
import { selectedCompanyIdAtom } from '../company';
import { selectedProjectAtom } from '../projects';
import { createCrudAtoms } from '../utils/crudFactory';

export const setActivityFeedSearchAtom = atom<string>('');

// Persistent cache for posts data
export const activityFeedPostsCacheAtom = atomWithStorage<IPost[]>('activityFeedPostsCache', []);
export const localActivityFeedDraftPostsAtom = atomWithStorage<Partial<IPost>[]>(
  'localActivityFeedDraftPosts',
  []
);

// Real-time Firestore listener for project feed using atomFamily
export const activityFeedPostsAtom = atomWithObservable<IPost[]>(
  (get) => {
    const project = get(selectedProjectAtom);
    const companyId = get(selectedCompanyIdAtom);

    // Use cached posts synchronously as an immediate first value
    const cached = get(activityFeedPostsCacheAtom);
    const initial: IPost[] = cached instanceof Promise ? [] : cached;

    if (!project?.id) {
      return new Observable<IPost[]>((subscriber) => {
        // Emit synchronously to avoid suspension
        subscriber.next(initial);
      });
    }

    const postQuery = query(
      collection(db, `companies/${companyId}/projects/${project.id}/activityFeed`),
      orderBy('createdAt', 'desc')
    );

    return new Observable<IPost[]>((subscriber) => {
      // Emit synchronously so reads never suspend on first access
      subscriber.next(initial);

      const unsubscribe = onSnapshot(postQuery, (snapshot) => {
        const postData: IPost[] = snapshot.docs.map((doc) =>
          IPost.fromDocumentData({ ...doc.data(), id: doc.id })
        );
        subscriber.next(postData);
      });

      return () => {
        unsubscribe();
      };
    });
  },
  {
    // Fallback initial value in case cached is a Promise
    initialValue: [],
  }
);

export const filteredActivityFeedPostsAtom = atomFamily((searchQuery: string) =>
  atom((get) => {
    const posts = get(activityFeedPostsAtom);

    if (!searchQuery.trim()) {
      return posts;
    }

    return posts.filter((post: IPost) => {
      if (postMatchesSearch(post, searchQuery)) {
        return true;
      }

      const comments = get(
        filteredPostCommentsAtom({ parentPath: `${post.parentPath}/${post.id}`, searchQuery })
      );
      return comments.some((comment) => commentMatchesSearch(comment, searchQuery));
    });
  })
);

// Sync latest Firestore data into cache every time postsAtom updates
export const activityFeedPostsSyncAtom = atom(null, async (get, set) => {
  const loaded = get(loadable(activityFeedPostsAtom));
  if (loaded.state === 'hasData') {
    set(activityFeedPostsCacheAtom, loaded.data);
  }
  // If loading or hasError, do nothing – avoid triggering Suspense from a write
});

export const addActivityFeedPostAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      newPost: Partial<IPost>;
      user: { displayName: string; avatarUrl: string; userId: string };
    }
  ) => {
    // Ensure Firebase authentication is valid
    let firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      const refreshSuccess = await refreshFirebaseToken();
      if (!refreshSuccess) {
        throw new Error('Failed to authenticate with Firebase');
      }
      firebaseUser = auth.currentUser;
    }

    const { newPost, user } = params;
    const project = get(selectedProjectAtom)!;
    const companyId = get(selectedCompanyIdAtom);

    // Use provided ID if available (from frontend-generated postId)
    // Otherwise generate a new document reference to get an ID
    let postDocRef;
    if (newPost.id) {
      postDocRef = doc(
        db,
        `companies/${companyId}/projects/${project.id}/activityFeed/${newPost.id}`
      );
    } else {
      postDocRef = doc(
        collection(db, `companies/${companyId}/projects/${project.id}/activityFeed`)
      );
    }

    const draftPost: Partial<IPost> = {
      id: postDocRef.id,
      projectId: project.id.toString(),
      companyId,
      isDeleted: false,
      isEdited: false,
      replyCount: 0,
      createdAt: new Date(),
      message: newPost.message || '',
      mentions: newPost.mentions || [],
      hashtags: newPost.hashtags || [],
      tags: newPost.tags || [],
      // visibility: newPost.visibility || 'public',
      likes: [],
      media: newPost.media || [],
      status: IPostStatus.DRAFT,
      postType: IPostType.USER_POST,
      createdBy: {
        userId: user?.userId || '',
        displayName: user?.displayName || '',
        avatarUrl: user?.avatarUrl || '',
      },
      // isLocalDraft: true,
    };

    // Add to local drafts immediately
    set(localActivityFeedDraftPostsAtom, (drafts) => [draftPost, ...drafts]);

    try {
      const finalPost = {
        ...draftPost,
        id: postDocRef.id,
        createdAt: serverTimestamp() as any,
        companyId: companyId,
        projectId: project.id.toString(),
        isDeleted: false,
        isEdited: false,
        // isLocalDraft: false,
      } as IPost;

      if (finalPost.message !== '<p></p>\n' || finalPost.media.length > 0) {
        await setDoc(postDocRef, finalPost);
        // Remove draft + rely on real-time Firestore update to insert final post
        set(localActivityFeedDraftPostsAtom, (drafts) =>
          drafts.filter((d) => d.id !== postDocRef.id)
        );
      }
    } catch (error) {
      console.error('Error creating post:', error);
      // Remove failed draft
      set(localActivityFeedDraftPostsAtom, (drafts) =>
        drafts.filter((d) => d.id !== postDocRef.id)
      );
      throw error;
    }
  }
);

export const updateActivityFeedPostAtom = atom(
  null,
  async (get, set, payload: { id: string; data: Partial<IPost> }) => {
    const project = get(selectedProjectAtom)!;
    const companyId = get(selectedCompanyIdAtom);

    const { updateAtom } = createCrudAtoms<IPost>(
      `companies/${companyId}/projects/${project.id}/activityFeed`
    );
    await set(updateAtom, payload);
  }
);

export const deleteActivityFeedPostAtom = atom(null, async (get, set, id: string) => {
  const project = get(selectedProjectAtom)!;
  const companyId = get(selectedCompanyIdAtom);

  const { deleteAtom } = createCrudAtoms<IPost>(
    `companies/${companyId}/projects/${project.id}/activityFeed`
  );
  await set(deleteAtom, id);
});

export const togglePostLikeAtom = atom(
  null,
  async (
    get,
    set,
    payload: {
      postId: string;
      userId: string;
      projectId: string;
      companyId: string;
    }
  ) => {
    // Check Firebase auth state first
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('No Firebase user authenticated');
      throw new Error('User must be authenticated to like posts');
    }

    const { postId, userId, projectId, companyId } = payload;

    if (!companyId || !projectId || !postId) {
      console.error('Company, Project or Post ID missing:', { companyId, projectId, postId });
      throw new Error('Company, Project and Post ID are required');
    }

    // Check what's in the cache
    const allPosts = get(activityFeedPostsAtom);

    const post = allPosts.find((p) => p.id === postId);

    if (!post) {
      console.error('Post not found with ID:', postId);
      console.error('Error getting post from Firestore:', postId);
      return;
    }

    const hasLiked = post.likes?.includes(userId) ?? false;
    const newlikes = hasLiked
      ? post.likes?.filter((uid) => uid !== userId)
      : [...(post.likes || []), userId];

    // Use direct Firestore update instead of createCrudAtoms
    try {
      await set(updateActivityFeedPostAtom, {
        id: postId,
        data: { likes: newlikes },
      });

      // // Update the cache to reflect the change
      // set(filePostsCacheAtom, (current) =>
      //   current.map(p =>
      //     p.id === postId
      //       ? { ...p, likes: newlikes }
      //       : p
      //   )
      // );
    } catch (error) {
      console.error('Error updating Firestore:', error);
      throw error;
    }
  }
);
